import { captureException } from '@sentry/react';
import { get } from 'lodash';
import { handleCleverUserClasses, handleCleverUserLogin, handleCleverUserRegistration } from 'store/clever/thunks';
import { handleLoginWithToken } from 'store/auth/thunks';

export const handleCleverAuth = async ({
    search,
    setIsAutoLogin,
    setError,
    setAutologinMessage,
    setLoading,
    loginWithSchoology,
    dispatch
}) => {
    setLoading(true);
    setIsAutoLogin(true);

    try {
        if (typeof search !== 'object' || !search?.code) {
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }
        if (typeof search.code !== 'string') {
            setAutologinMessage('Invalid code. Please try again or contact support for assistance.');
            setError('Invalid code. Please try again or contact support for assistance.');
            setLoading(false);
            setIsAutoLogin(false);
            captureException('Invalid code in handleCleverAuth');
            return;
        }

        const cleverResponse = await handleCleverUserLogin(search.code);
        if (!dispatch) {
            setAutologinMessage('Unable to retrieve the user. Please try again or contact support for assistance.');
            setError('Unable to retrieve the user. Please try again or contact support for assistance.');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }

        if (cleverResponse?.token) {
            await handleClever(cleverResponse, {
                setAutologinMessage,
                setError,
                setLoading,
                setIsAutoLogin,
                loginWithSchoology,
                dispatch
            });
            return;
        }

        const cleverUserId = get(cleverResponse, 'data.id');
        const accessToken = cleverResponse?.access_token;
        if (cleverUserId && accessToken) {
            await handleRegisterCleverUser(cleverUserId, accessToken, {
                setAutologinMessage,
                setError,
                setLoading,
                setIsAutoLogin,
                loginWithSchoology,
                dispatch
            });
            return;
        }

        setAutologinMessage('Unable to retrieve the user. Please try again or contact support for assistance.');
        setError('Unable to retrieve the user. Please try again or contact support for assistance.');
        setLoading(false);
        setIsAutoLogin(false);
    } catch (error) {
        captureException(`591 Error in handleCleverAuth: ${error}`);
        setAutologinMessage('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setError('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

const handleClever = async (users, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch }) => {
    try {
        const cleverUserId = get(users, 'data.id');
        const accessToken = users.access_token;

        setAutologinMessage('User found. Fetching user sections…');
        if (cleverUserId && accessToken) {
            await handleCleverUserClasses(cleverUserId, accessToken);
        }

        setAutologinMessage('Signing you in…');
        const result = await dispatch(handleLoginWithToken(users.token));
        if (!result || !result.isSignedIn) {
            setAutologinMessage('Authentication failed. Please try login again!');
            setError('Authentication failed. Please try login again!');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }
        const userInfo = get(users, 'data.user_info');
        const userData = userInfo ? { username: result.username, ...userInfo } : { username: result.username };
        await loginWithSchoology(userData);
        setLoading(false);
        setIsAutoLogin(false);
    } catch (error) {
        captureException(`593 Error in handleClever: ${error}`);
        setAutologinMessage('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setError('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

const handleRegisterCleverUser = async (id, accessToken, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch }) => {
    try {
        const registedUserData = await handleCleverUserRegistration(id, accessToken);

        if (!registedUserData?.id || !registedUserData?.token) {
            setAutologinMessage('Unable to register this Clever user. Please try again or contact support for assistance.');
            setError('Unable to register this Clever user. Please try again or contact support for assistance.');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }

        try {
            setAutologinMessage('User registered successfully. Fetching user sections…');
            await handleCleverUserClasses(id, accessToken);
        } catch (error) {
            captureException(`592 Error in handling userclass: ${error}`);
        }

        setAutologinMessage('Signing you in…');
        const result = await dispatch(handleLoginWithToken(registedUserData.token));
        if (!result || !result.isSignedIn) {
            setAutologinMessage('Authentication failed. Please try login again!');
            setError('Authentication failed. Please try login again!');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }

        const userData = registedUserData.data ? { ...registedUserData.data, username: result.username } : { username: result.username };
        await loginWithSchoology(userData);
        setLoading(false);
        setIsAutoLogin(false);
    } catch (error) {
        captureException(`594 Error in handleRegisterCleverUser: ${error}`);
        setAutologinMessage('Unable to register this Clever user. Please try again or contact support for assistance.');
        setError('Unable to register this Clever user. Please try again or contact support for assistance.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};
